import { useQuery } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import ReceiptToolsLayoutShellButton from 'src/components/ReceiptToolsDropdown/ReceiptToolsLayoutShellButton'
import ReportTransactionsBoxAndLinks from 'src/components/ReportTransactionsBoxAndLinks/ReportTransactionsBoxAndLinks'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import { TransactionsTableLargeDesigns } from 'src/components/TransactionsDataGrid/TransactionsDataGrid'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { TransactionFilterOptions } from 'src/enums'
import { useManagerReviewRulesEnabled } from 'src/feature/ManagerReview/managerReviewRules.queries'
import { useEventListener } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { useQueryPeopleManagerDirectReports } from 'src/queries/organizations/peopleManagers.queries'
import { orgReceiptManagementQueries } from 'src/queries/organizations/receiptManagement/receiptManagement.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const ExpensesNeedsApproval: React.FC = () => {
  const translate = useTranslate()

  const user = useCurrentUser()
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const { results: statistics, anyLoading: reactQueryStatisticsLoading } =
    useQueryStatistics({
      statisticsKeys: [
        StatisticsKeys.CreditCardsSourcePhysicalClearingsLinkedCount,
      ],
    })
  const creditCardsSourcePhysicalClearingsLinkedCount =
    statistics?.CreditCardsSourcePhysicalClearingsLinkedCount?.data ?? 0

  const appName = useSelector(selectAppName)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const { data: receiptManagementEnabled } = useQuery(
    orgReceiptManagementQueries.enabled(organization?.id),
  )

  const [initialLoad, setInitialLoad] = useState(true)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })
  const clientHeight = useMemo(
    () => viewportHeight.height - 122,
    [viewportHeight],
  )

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEventListener('resize', updateWindowSize)

  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()
  const { data: directReports } = useQueryPeopleManagerDirectReports({
    organizationId: user?.organizationId,
    userId: user?.id,
  })
  const showManagerReviewContent =
    directReports !== undefined &&
    directReports.length > 0 &&
    managerReviewRulesEnabled

  const content = reactQueryStatisticsLoading ? (
    <LoadingBackgroundPage />
  ) : showManagerReviewContent ? (
    <>
      <Helmet>
        <title>{`Transactions | ${appName}`}</title>
        <meta name="description" content={`Transactions page of ${appName}`} />
      </Helmet>
      <BackgroundPageContent noStackingIndex noPadding>
        <ReportTransactionsBoxAndLinks
          context={TransactionReportType.NeedsApproval}
          showIntegrationsLink
          showConnection
          design={TransactionsTableLargeDesigns.UserAndCard}
          initialLoad={initialLoad}
          setInitialLoad={setInitialLoad}
          visibleFilters={[
            TransactionFilterOptions.TransactionAmountRange,
            // using creditCardsSourcePhysicalClearingsLinkedCount > 0 check until BE updates Regions cards to accurately set physicalClearingsEnabled
            ...(organization?.physicalClearingsEnabled ||
            creditCardsSourcePhysicalClearingsLinkedCount > 0
              ? [TransactionFilterOptions.TransactionOrigin]
              : []),
            TransactionFilterOptions.ExpenseCategoryStatus,
            TransactionFilterOptions.ExpenseCategoryLabels,
            ...(receiptManagementEnabled !== undefined
              ? [TransactionFilterOptions.ReceiptStatus]
              : []),
            ...(activeIntegration !== undefined &&
            user?.hasOrgBookkeeperPermissions
              ? [
                  TransactionFilterOptions.TransactionSyncStatus,
                  TransactionFilterOptions.ReceiptSyncStatus,
                ]
              : []),
            TransactionFilterOptions.CardUser,
            TransactionFilterOptions.MerchantCategory,
            TransactionFilterOptions.Search,
          ]}
          clientHeight={clientHeight || 0}
        />
      </BackgroundPageContent>
    </>
  ) : (
    <NotFoundBackgroundPageContent />
  )

  const buttons =
    receiptManagementEnabled && organization !== undefined
      ? [
          <ReceiptToolsLayoutShellButton
            key={'receipt-tools-layout-button'}
            organization={organization}
            hasReceiptRulePermissions={!!user?.hasOrgBookkeeperPermissions}
          />,
        ]
      : []

  return (
    <LayoutShell
      subnavDesign={SubnavDesign.Floating}
      subnavOptions={[]}
      content={content}
      title={translate('Card Transactions')}
      // subtitle={translate('Get visibility into your team’s spend and flag anything that needs a second look')}
      buttons={buttons}
    />
  )
}

export default ExpensesNeedsApproval
